import { color, redcapFlagTheme } from '../styles/theme';
import { num, plainDate, shortDate } from '../lib/format';
import { useRedcapWeeks } from '../api/redcap';
import { Button, Card, CardIcon } from '../components/ui';
import { IconRedcap } from '../components/icons';
import { Pill } from './RedcapShared';

/**
 * REDCap this week, on the Dashboard: one line per linked study — which
 * Monday's file is in, and how much there is to talk about. Opens the study's
 * week. Renders nothing when no study is linked: an empty card would be a
 * placeholder, not information.
 */
export function RedcapWeekCard({ onOpen }: { onOpen: (study: string) => void }) {
  const weeks = useRedcapWeeks();
  const studies = weeks.data?.studies ?? [];
  if (weeks.isLoading || weeks.error || studies.length === 0) return null;

  return (
    <Card pad="20px 22px">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <CardIcon>
          <IconRedcap size={18} />
        </CardIcon>
        <span style={{ fontSize: 14.5, fontWeight: 800, color: color.ink, flex: 1 }}>REDCap this week</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', marginTop: 10 }}>
        {studies.map((s) => {
          const late = s.lastAsOf !== null && !s.isCurrentWeek && (s.uploadAgeDays ?? 0) >= 8;
          return (
            <div
              key={s.studySlug}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
                padding: '9px 0',
                borderTop: `1px solid ${color.line}`,
              }}
            >
              <Button
                variant="ghost"
                onClick={() => onOpen(s.studySlug)}
                style={{ padding: '5px 12px', fontSize: 12.5, flex: 'none' }}
                title={`Open ${s.short}’s week`}
              >
                {s.short}
              </Button>
              <span style={{ fontSize: 12.5, color: late ? redcapFlagTheme.urgent.fg : color.muted, flex: 1, minWidth: 160 }}>
                {s.lastAsOf === null
                  ? 'No week uploaded yet'
                  : late
                    ? `This Monday’s file has not arrived · newest is ${num(s.uploadAgeDays ?? 0)} days old`
                    : `Week of ${plainDate(s.lastAsOf)}${s.uploadedAt ? ` · uploaded ${shortDate(s.uploadedAt)}` : ''}`}
              </span>
              <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {s.late.urgent > 0 && <Pill tone={redcapFlagTheme.urgent}>{num(s.late.urgent)} urgent</Pill>}
                {s.late.watch > 0 && <Pill tone={redcapFlagTheme.watch}>{num(s.late.watch)} to watch</Pill>}
                {s.lastAsOf !== null && s.late.urgent === 0 && s.late.watch === 0 && (
                  <span style={{ fontSize: 12, color: color.muted }}>Nothing to talk about</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
